import React, { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { Shield, Lock, User, Eye, EyeOff, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Login() {
  const { login, isAuthed } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [phase, setPhase] = useState("idle");

  useEffect(() => {
    if (isAuthed) nav(loc.state?.from || "/", { replace: true });
  }, [isAuthed, nav, loc]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr("");
    setBusy(true);
    setPhase("verifying");
    const res = await login(u.trim(), p);
    if (!res.ok) {
      setBusy(false);
      setPhase("idle");
      setErr(res.error);
      return;
    }
    setPhase("success");
    nav(loc.state?.from || "/", { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row" data-testid="login-page" style={{ background: "#06192C" }}>
      {/* Left: brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between w-[44%] p-10 xl:p-14 overflow-hidden text-white"
        style={{ background: "linear-gradient(135deg, #06192C 0%, #082B4D 55%, #0E3F70 100%)" }}>
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
          backgroundSize: "44px 44px",
        }} />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #18B84F 0%, transparent 70%)" }} />
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 70%)" }} />

        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #18B84F 0%, #0E8939 100%)", boxShadow: "0 8px 28px -8px rgba(24,184,79,0.55)" }}>
              <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-tight">HSE Command Center</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#18B84F] font-semibold">& Reporting System</div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#18B84F] font-semibold mb-3">
            Enterprise HSE Platform
          </div>
          <h1 className="font-heading font-bold text-4xl xl:text-5xl tracking-tight leading-[1.05]">
            Health, Safety<br />
            <span className="text-[#18B84F]">& Environment.</span>
          </h1>
          <p className="text-white/60 text-[14px] leading-relaxed mt-5 max-w-md">
            Unified command-center reporting and compliance for enterprise HSE management.
          </p>
        </div>

        <div className="relative text-[11px] text-white/40 font-mono">
          © {new Date().getFullYear()} HSE Command Center · Powered by NoonGravity
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative" style={{ background: "#F5F7F9" }}>
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #18B84F 0%, #0E8939 100%)" }}>
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-bold text-[#082B4D]">HSE Command Center</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#18B84F] font-semibold">& Reporting System</div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.2em] text-[#18B84F] font-bold mb-2">Welcome back</div>
          <h2 className="font-heading font-bold text-3xl text-[#06192C] tracking-tight mb-1">Sign in to continue</h2>
          <p className="text-slate-500 text-sm mb-8">Enter your administrator credentials to access the command center.</p>

          {err && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12px]" data-testid="login-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="font-medium">{err}</span>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Username</label>
              <div className="mt-1 relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="login-username"
                  value={u}
                  onChange={(e) => setU(e.target.value)}
                  placeholder="Enter username"
                  autoComplete="username"
                  required
                  disabled={busy}
                  className="pl-9 h-11 text-sm bg-white border-slate-200 focus-visible:ring-[#18B84F]"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                <span>Password</span>
                <button type="button" onClick={() => setShow((x) => !x)} className="text-[10px] uppercase tracking-wider text-[#18B84F] hover:text-[#0E8939] flex items-center gap-1" data-testid="login-toggle-show">
                  {show ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Show</>}
                </button>
              </label>
              <div className="mt-1 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  value={p}
                  onChange={(e) => setP(e.target.value)}
                  placeholder="Enter password"
                  autoComplete="current-password"
                  required
                  disabled={busy}
                  className="pl-9 h-11 text-sm bg-white border-slate-200 focus-visible:ring-[#18B84F]"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={busy || !u || !p}
            className="mt-6 w-full h-12 rounded-xl font-heading font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all relative overflow-hidden disabled:opacity-60"
            style={{
              background: phase === "success"
                ? "linear-gradient(135deg, #18B84F 0%, #0E8939 100%)"
                : "linear-gradient(135deg, #082B4D 0%, #0E3F70 50%, #18B84F 130%)",
              boxShadow: "0 10px 28px -10px rgba(8,43,77,0.45), 0 6px 16px -8px rgba(24,184,79,0.4)",
            }}
          >
            {phase === "idle" && (<><Shield className="w-4 h-4" /> Sign in securely <ArrowRight className="w-4 h-4 ml-1" /></>)}
            {phase === "verifying" && (
              <>
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: "240ms" }} />
                </span>
                Verifying secure session…
              </>
            )}
            {phase === "success" && (<><CheckCircle2 className="w-4 h-4" /> Access granted</>)}
          </button>

          <div className="mt-5 flex items-center gap-2 text-[11px] text-slate-500">
            <Lock className="w-3 h-3 text-[#18B84F]" />
            <span>Protected administrator session</span>
          </div>
        </form>
      </div>
    </div>
  );
}
